
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import ChevronDownIcon from "./ChevronDownIcon";
import CalendarJoinIcon from "./icons/CalendarJoinIcon";
import { truncateName } from "@/utils/truncateName";
import AppStorePopUp from "@/components/shared/AppStorePopUp"; 

export function UserProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false); 
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // If not authenticated, don't render
  if (!isAuthenticated || !user) {
    return null;
  }

  const formatJoinDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", { month: "short", year: "numeric" })
      .toLowerCase();
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };


  const handleMenuItemClick = (action: string, path?: string) => {
    setIsOpen(false); 
    

    if (action === 'orders' || action === 'addresses') {
      setIsPopupOpen(true);
    } else if (action === 'signout') {
      handleSignOut();
    } else if (path) {
      navigate(path);
    }
  };

  // Construct user display data from auth user object
  const userName = `${user.firstName} ${user.lastName}`;
  const userAvatar = user.profilePictureUrl || "";
  const joinedDate = user.createdAt;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-[8px] outline-none focus:outline-none">
            <div className="hidden md:flex flex-col items-start gap-[2px]">
              <span className="text-[15px] font-lexend font-medium tracking-[-0.22px] capitalize text-text-primary">
                {truncateName(userName)}
              </span>
              <div className="flex items-center gap-[4px]">
                <img
                  src="./images/navbar/calendar-join.svg"
                  alt="Calendar"
                  width={11}
                  height={11}
                />
                <span className="text-[12px] font-lexend font-light tracking-[-0.22px] capitalize text-text-secondary">
                  joined {formatJoinDate(joinedDate)}
                </span>
              </div>
            </div>
            <Avatar className="w-6 h-6 md:w-[41px] md:h-[41px]">
              {userAvatar ? (
                <AvatarImage src={userAvatar} alt={userName} />
              ) : null}
              <AvatarFallback className="text-xs md:text-base bg-primary/10">
                {user.firstName.charAt(0).toUpperCase()}
                {user.lastName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <ChevronDownIcon
              width={14}
              height={8}
              color="rgba(0, 0, 0, 0.50)"
              className={`hidden md:block transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[calc(100vw-2rem)] max-w-[14.7rem] p-0 rounded-[5px] border-[rgba(0,0,0,0.05)] shadow-[0px_4px_4px_rgba(0,0,0,0.25)]"
        >
          {/* User Profile Header */}
          <div className="flex flex-col items-center gap-4 sm:gap-4 pt-2 sm:pt-[10px] pb-3 sm:pb-3">
            <Avatar className="w-16 sm:w-20 h-16 sm:h-20">
              {userAvatar ? (
                <AvatarImage
                  src={userAvatar}
                  alt={userName}
                  className="object-cover"
                />
              ) : null}
              <AvatarFallback className="text-xl sm:text-2xl bg-primary/10">
                {user.firstName.charAt(0).toUpperCase()}
                {user.lastName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-center gap-2 sm:gap-[9px]">
              <div className="max-w-[180px] text-center break-all">
                <h3 className="font-lexend text-[14px] sm:text-[15px] font-medium tracking-[-0.22px] capitalize text-black">
                  {userName}
                </h3>
              </div>
              <div className="flex items-center gap-1">
                <CalendarJoinIcon
                  width={9}
                  height={9}
                  color="rgba(0, 0, 0, 0.50)"
                />
                <span className="font-lexend text-[11px] sm:text-[12px] font-light tracking-[-0.22px] capitalize text-black/50">
                  joined {formatJoinDate(joinedDate)}
                </span>
              </div>
            </div>
          </div>


          <DropdownMenuItem
            onClick={() => handleMenuItemClick('profile', '/profile')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            Auqli Profile
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-0 bg-black/20" />

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('saved-blips', '/saved-blips')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            Saved Blips
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('orders', '/orders')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            My Orders
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('wishlist', '/wishlist')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            My Wishlist
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('following', '/following')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            Following
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('addresses', '/addresses')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            Delivery Address
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-3 bg-black/20 " />

          <DropdownMenuItem
            onClick={() => handleMenuItemClick('signout')}
            className="px-4 sm:px-5 py-2.5 sm:py-3 font-lexend text-[12px] sm:text-[13px] font-medium tracking-[-0.22px] capitalize text-black/80 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 rounded-none"
          >
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>


      <AppStorePopUp open={isPopupOpen} onOpenChange={setIsPopupOpen} />
    </>
  );
}